import * as RadixTooltip from '@radix-ui/react-tooltip'
import { addDays, format, getTime } from 'date-fns'
import {
  bool,
  func,
  instanceOf,
  number,
  object,
  oneOfType,
  shape,
  string,
} from 'prop-types'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { renderBreakdownTooltipContent } from '@opallabs/calendar'
import { Timescale } from '@opallabs/concepts'
import { kuiperClassNames } from '@opallabs/kuiper'
import { formatWeekHeading } from '../HistogramBar'
import Day from './Day'
import { weekTooltipContentStyle } from './styles'

import './styles.scss'

const Week = ({
  breakdown,
  draggedMomentParams,
  firstDayOfWeek,
  histogramData,
  launchingData,
  isContentMode,
  isFocused,
  largestDayCount,
  onClick,
  onRescheduleContent,
  onUpdateMoment,
  story,
  style,
}) => {
  const { weekTotal, weekBreakdown, lastDayOfWeek } = useMemo(() => {
    const firstKey = format(firstDayOfWeek, 'yyyy-MM-dd')
    let inMarketSum = 0
    let launchingSum = 0
    for (let i = 0; i < 7; i += 1) {
      const key = format(addDays(firstDayOfWeek, i), 'yyyy-MM-dd')
      inMarketSum += histogramData?.[key] ?? 0
      launchingSum += launchingData?.[key] ?? 0
    }
    // Carried-in: content in-market on the week's first day but launched
    // earlier. Used for non-focused weeks, which have no client breakdown.
    const carriedIn = Math.max(
      0,
      (histogramData?.[firstKey] ?? 0) - (launchingData?.[firstKey] ?? 0)
    )
    // Only the focused week receives a populated breakdown; its rows are the
    // source of truth for the tooltip in content mode.
    const focusedBreakdown = (isFocused && breakdown?.weekBreakdown) || {}

    // Content mode reconciles the header total with the tooltip rows by
    // summing the same breakdown. Non-focused weeks (no breakdown) fall back
    // to the server histogram — launching plus carried-in, so a span counts
    // once. Moment mode has no spans: the in-market sum is correct.
    let weekTotal
    if (!isContentMode) weekTotal = inMarketSum
    else if (isFocused)
      weekTotal = Object.values(focusedBreakdown).reduce((sum, n) => sum + n, 0)
    else weekTotal = launchingSum + carriedIn

    return {
      weekTotal,
      weekBreakdown: focusedBreakdown,
      lastDayOfWeek: addDays(firstDayOfWeek, 6),
    }
  }, [
    breakdown,
    firstDayOfWeek,
    histogramData,
    launchingData,
    isContentMode,
    isFocused,
  ])

  const weekTooltipContent = renderBreakdownTooltipContent({
    breakdown: weekBreakdown,
    colorFor: breakdown?.colorFor,
    heading: formatWeekHeading(firstDayOfWeek, lastDayOfWeek),
    isContentMode: Boolean(isContentMode),
    nameFor: breakdown?.nameFor,
    parentOf: breakdown?.parentOf,
    totalCount: weekTotal,
  })

  // Raw Radix with a controlled `open` — Lapidary's wrapper can't
  // re-open after bar→empty-week (no new pointerenter fires on the
  // already-hovered Week trigger).
  const [isAnyBarHovered, setIsAnyBarHovered] = useState(false)
  const [isWeekHovered, setIsWeekHovered] = useState(false)
  const [hasHoverDelayElapsed, setHasHoverDelayElapsed] = useState(false)

  const handleBarHoverChange = useCallback((open) => {
    setIsAnyBarHovered(open)
  }, [])

  // Anti-flicker delay on first enter only; stays elapsed until the
  // pointer leaves the week, so bar→week transitions re-open instantly.
  useEffect(() => {
    if (!isWeekHovered) {
      setHasHoverDelayElapsed(false)
      return undefined
    }
    const timer = setTimeout(() => setHasHoverDelayElapsed(true), 100)
    return () => clearTimeout(timer)
  }, [isWeekHovered])

  const isWeekTooltipOpen =
    isWeekHovered && hasHoverDelayElapsed && !isAnyBarHovered

  /* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
  return (
    <RadixTooltip.Provider>
      <RadixTooltip.Root open={isWeekTooltipOpen}>
        <RadixTooltip.Trigger asChild>
          <div
            style={style}
            onClick={onClick}
            onMouseEnter={() => setIsWeekHovered(true)}
            onMouseLeave={() => setIsWeekHovered(false)}
            className={kuiperClassNames('histogramWeek', { isFocused })}
          >
            <Fragment>
              {new Array(7).fill({}).map((v, dayIndex) => {
                const thisDay = addDays(firstDayOfWeek, dayIndex)
                const dayKey = format(thisDay, 'yyyy-MM-dd')
                const daySize = histogramData[dayKey] || 0
                // 52px: the band, less the strip the month label and the day
                // number take above it.
                const dayHeight = 52 * (daySize / largestDayCount)
                const isFirstDayOfWeek = dayIndex === 0
                const isLastDayOfWeek = dayIndex === 6

                return (
                  <Day
                    breakdown={isFocused ? breakdown : undefined}
                    date={thisDay}
                    dayHeight={dayHeight}
                    daySize={daySize}
                    draggedMomentParams={draggedMomentParams}
                    isContentMode={isContentMode}
                    isFirstDayOfWeek={isFirstDayOfWeek}
                    isLastDayOfWeek={isLastDayOfWeek}
                    // Once the week tooltip has cleared its anti-flicker
                    // delay, bars open instantly. Keeps transitions snappy.
                    isTooltipFastTrack={hasHoverDelayElapsed}
                    key={getTime(thisDay)}
                    mode={Timescale.Week}
                    onBarHoverChange={handleBarHoverChange}
                    onRescheduleContent={onRescheduleContent}
                    onUpdateMoment={onUpdateMoment}
                    story={story}
                  />
                )
              })}
            </Fragment>
          </div>
        </RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            side="bottom"
            sideOffset={12}
            style={{ zIndex: 998 }}
          >
            <div css={weekTooltipContentStyle}>{weekTooltipContent}</div>
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  )
  /* eslint-enable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
}

Week.propTypes = {
  breakdown: object,
  draggedMomentParams: shape({
    id: string.isRequired,
    scheduledAt: oneOfType([instanceOf(Date), string]).isRequired,
    primaryStoryInterval: shape({
      start: instanceOf(Date).isRequired,
      end: instanceOf(Date).isRequired,
    }),
  }),
  firstDayOfWeek: instanceOf(Date),
  histogramData: object,
  launchingData: object,
  isContentMode: bool,
  isFocused: bool,
  largestDayCount: number,
  onClick: func,
  onRescheduleContent: func,
  onUpdateMoment: func,
  story: object,
  style: object,
}

export default Week
